import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Cal, { getCalApi } from "@calcom/embed-react";
import { CalendarDays, Clock, Globe2, ShieldCheck, CheckCircle2, ArrowUpRight } from "lucide-react";
import { GoldDivider, Eyebrow } from "../../components/brand";
import { track } from "../../lib/analytics";

/**
 * Consultation booking page.
 *
 * Uses an inline Cal.com embed (@calcom/embed-react) styled in-brand. Cal.com
 * writes the booking to a shared Google Calendar (`alturaglobalre@gmail.com`)
 * and fires a webhook → `/api/public/consultations/cal-webhook` which the
 * backend uses to update the CRM consultation record and dispatch our
 * branded confirmation email + internal notification via Resend.
 *
 * Config comes from build-time env vars so ops can rotate the Cal.com
 * username/slug without a code change:
 *   REACT_APP_CAL_LINK           e.g. "altura-global/consultation"
 *   REACT_APP_CAL_NAMESPACE      e.g. "altura-consultation"  (optional)
 *
 * Fallback: if REACT_APP_CAL_LINK is not set, we render a graceful
 * "coming soon" panel with a mailto — the page NEVER dead-ends.
 */
export default function Consultation() {
  const [params] = useSearchParams();
  const [confirmed, setConfirmed] = useState(false);

  const calLink = process.env.REACT_APP_CAL_LINK;
  const calNamespace = process.env.REACT_APP_CAL_NAMESPACE || "altura-consultation";
  const embedEnabled = Boolean(calLink);

  // Prefill from query params if the visitor arrived from a lead form or
  // AURI recommendation. Cal.com accepts prefill via config.
  const prefill = useMemo(() => {
    const p = {};
    if (params.get("name")) p.name = params.get("name");
    if (params.get("email")) p.email = params.get("email");
    if (params.get("notes")) p.notes = params.get("notes");
    return p;
  }, [params]);

  useEffect(() => {
    (async function initCal() {
      try {
        const cal = await getCalApi({ namespace: calNamespace });
        cal("ui", {
          theme: "light",
          hideEventTypeDetails: false,
          layout: "month_view",
          styles: {
            branding: { brandColor: "#b38f46" },
          },
          cssVarsPerTheme: {
            light: {
              "cal-brand": "#b38f46",
              "cal-text": "#1c1c1c",
              "cal-text-emphasis": "#3a1f14",
              "cal-bg": "#ffffff",
              "cal-bg-emphasis": "#fbf8f2",
              "cal-border": "#eee1c6",
              "cal-border-emphasis": "#b38f46",
            },
          },
        });
        // Listen for the successful booking event fired by the embed. This is
        // a fast UX signal — the authoritative record is created by the
        // backend webhook, but this lets us show the confirmation immediately.
        cal("on", {
          action: "bookingSuccessful",
          callback: () => { setConfirmed(true); track.consultationBooked(); },
        });
      } catch (e) {
        // If the embed script fails to load (network, adblock), we still
        // render the shell — just without the interactive picker.
      }
    })();
  }, [calNamespace]);

  return (
    <div data-testid="page-consultation">
      {/* Hero */}
      <section className="bg-altura-espresso text-altura-ivory pt-24 pb-16 relative noise overflow-hidden">
        <div className="pointer-events-none absolute -right-32 -top-40 h-[520px] w-[520px] rounded-full border border-altura-gold/15 opacity-70" />
        <div className="container-editorial relative z-10 max-w-4xl">
          <Eyebrow className="text-altura-gold">Advisory</Eyebrow>
          <h1 className="mt-4 font-editorial text-4xl sm:text-5xl lg:text-6xl leading-[1.02] text-altura-ivory">
            Schedule a private consultation.
          </h1>
          <GoldDivider className="mt-6" />
          <p className="mt-6 text-altura-ivory/75 max-w-2xl leading-relaxed">
            Choose a time that works for you — a member of the Altura Global advisory team will meet you 1:1 to discuss your global real-estate goals with clarity and confidence.
          </p>
          <div className="mt-8 flex flex-wrap gap-6 text-sm text-altura-ivory/70">
            <span className="inline-flex items-center gap-2"><Clock className="h-4 w-4 text-altura-gold" /> Private 1:1 session</span>
            <span className="inline-flex items-center gap-2"><Globe2 className="h-4 w-4 text-altura-gold" /> Video or in-person</span>
            <span className="inline-flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-altura-gold" /> Confidential &amp; personal</span>
          </div>
        </div>
      </section>

      {/* Embed or Confirmation */}
      <section className="bg-altura-ivory py-14">
        <div className="container-editorial max-w-4xl">
          {confirmed ? (
            <ConfirmationCard />
          ) : embedEnabled ? (
            <div className="rounded-lg border border-altura-espresso/10 bg-white p-3 md:p-6 shadow-[0_30px_80px_-20px_rgba(58,31,20,0.15)]"
                 data-testid="cal-embed-wrapper">
              <Cal
                namespace={calNamespace}
                calLink={calLink}
                style={{ width: "100%", height: "700px", minHeight: "700px", overflow: "hidden" }}
                config={{
                  layout: "month_view",
                  ...prefill,
                }}
              />
            </div>
          ) : (
            <BookingComingSoon />
          )}
        </div>
      </section>

      {/* What to expect */}
      <section className="bg-white border-t border-altura-espresso/5 py-16">
        <div className="container-editorial max-w-4xl">
          <Eyebrow>What to expect</Eyebrow>
          <div className="mt-8 grid gap-6 md:grid-cols-3">
            <ExpectCard num="1" title="Pick your slot" body="Real live availability. Choose a window that suits your timezone — you'll see instant confirmation and a calendar invite arrives in your inbox." />
            <ExpectCard num="2" title="Personal conversation" body="A dedicated advisor will meet you 1:1 (video or in-person, your choice). Bring your goals — we'll bring clarity on markets, structures, and next steps." />
            <ExpectCard num="3" title="Actionable follow-up" body="You'll leave with a personalized shortlist of opportunities to explore, plus a written recap and any resources tailored to your situation." />
          </div>
        </div>
      </section>
    </div>
  );
}

function ExpectCard({ num, title, body }) {
  return (
    <div className="rounded-lg border border-altura-espresso/10 bg-altura-ivory p-6">
      <div className="text-[10px] uppercase tracking-[0.2em] text-altura-gold-dark">Step {num}</div>
      <h3 className="mt-3 font-editorial text-xl text-altura-espresso leading-tight">{title}</h3>
      <p className="mt-2 text-sm text-altura-charcoal/70 leading-relaxed">{body}</p>
    </div>
  );
}

function ConfirmationCard() {
  return (
    <div className="rounded-lg border border-altura-gold/40 bg-altura-gold/5 p-10 text-center max-w-2xl mx-auto" data-testid="consultation-confirmed">
      <div className="mx-auto w-14 h-14 rounded-full bg-altura-gold/20 flex items-center justify-center">
        <CheckCircle2 className="h-7 w-7 text-altura-gold-dark" />
      </div>
      <div className="eyebrow text-altura-gold-dark mt-6 mx-auto">Booked</div>
      <h2 className="mt-3 font-editorial text-3xl text-altura-espresso">Your consultation is confirmed.</h2>
      <p className="mt-4 text-altura-charcoal/70 leading-relaxed">
        A calendar invitation has been sent to your inbox with all the meeting details. We'll be in touch a day before to confirm — please reply to that email with anything you'd like us to prepare in advance.
      </p>
      <div className="mt-8 flex flex-wrap justify-center gap-3">
        <Link to="/resources" className="btn-outline-gold" data-testid="post-book-resources">
          Explore the Digital Library <ArrowUpRight className="h-4 w-4" />
        </Link>
        <Link to="/opportunities" className="btn-gold" data-testid="post-book-opportunities">
          Browse Opportunities <ArrowUpRight className="h-4 w-4" />
        </Link>
      </div>
    </div>
  );
}

function BookingComingSoon() {
  // Graceful fallback when REACT_APP_CAL_LINK is not yet configured. Ensures
  // the CTA is never a dead-end.
  return (
    <div className="rounded-lg border border-altura-espresso/10 bg-white p-10 text-center max-w-2xl mx-auto" data-testid="consultation-fallback">
      <CalendarDays className="mx-auto h-8 w-8 text-altura-gold-dark" />
      <h2 className="mt-4 font-editorial text-2xl text-altura-espresso">Live scheduling comes online shortly.</h2>
      <p className="mt-3 text-altura-charcoal/70 leading-relaxed">
        In the meantime, share a few details with us and an advisor will reach out within 24 hours to schedule your session directly.
      </p>
      <div className="mt-8">
        <Link to="/contact?type=consultation" className="btn-gold">
          Send us your details <ArrowUpRight className="h-4 w-4" />
        </Link>
      </div>
    </div>
  );
}
